import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import {
  Loader2,
  Sparkles,
  Layers,
  ArrowRight,
  Wand2,
} from "lucide-react";
import { toast } from "sonner";

export default function Presets() {
  const [presets, setPresets] = useState(null);
  const [creatingId, setCreatingId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get("/presets")
      .then((r) => setPresets(r.data.presets || []))
      .catch(() => setPresets([]));
  }, []);

  const quickCreate = async (preset) => {
    setCreatingId(preset.id);
    try {
      const r = await api.post(`/presets/${preset.id}/create-series`);
      toast.success(
        `Series created — ${preset.title}: ${r.data.topic_count} topics ` +
          `(~$${r.data.estimated_cost_usd.toFixed(2)})`,
      );
      navigate(`/series/${r.data.series_id}`);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not create series from preset",
      );
    } finally {
      setCreatingId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="presets-page">
      <div>
        <h1 className="text-3xl font-bold text-stone-800 sm:text-4xl">
          Launch Pack
        </h1>
        <p className="mt-1 max-w-2xl text-stone-600">
          One-click curated topic bundles encoding the kids-channel winning
          formula. Pick a pack, confirm the schedule, and Dom Studio queues up
          5–10 fully-animated videos.
        </p>
      </div>

      {!presets ? (
        <div className="flex items-center gap-2 text-stone-500">
          <Loader2 className="h-4 w-4 animate-spin" /> loading presets…
        </div>
      ) : presets.length === 0 ? (
        <div className="rounded-lg border border-dashed border-stone-300 p-8 text-center text-stone-500">
          No presets available — check back later.
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {presets.map((p) => (
            <Card
              key={p.id}
              data-testid={`preset-card-${p.id}`}
              className="group relative flex flex-col overflow-hidden border-orange-200 bg-white/85 transition hover:-translate-y-0.5 hover:shadow-lg"
            >
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-orange-400 via-rose-400 to-amber-400" />
              <CardContent className="flex flex-1 flex-col gap-3 p-5">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-3xl">{p.emoji}</div>
                  <Badge
                    variant="outline"
                    className="border-stone-300 text-[11px] uppercase tracking-wide text-stone-600"
                  >
                    {p.category}
                  </Badge>
                </div>
                <div>
                  <div className="text-lg font-bold text-stone-800">
                    {p.title}
                  </div>
                  <div className="text-xs text-stone-500">{p.tagline}</div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {p.topics.slice(0, 5).map((t) => (
                    <span
                      key={t.topic_key}
                      className="rounded bg-stone-100 px-1.5 py-0.5 text-[11px] text-stone-700"
                    >
                      {t.label}
                    </span>
                  ))}
                  {p.topics.length > 5 ? (
                    <span className="rounded bg-stone-200 px-1.5 py-0.5 text-[11px] font-medium text-stone-700">
                      +{p.topics.length - 5} more
                    </span>
                  ) : null}
                </div>
                <div className="mt-auto flex items-center justify-between gap-2 pt-2 text-xs text-stone-600">
                  <span>
                    <Layers className="mr-1 inline h-3 w-3" />
                    {p.topic_count} videos · {p.content_type}
                  </span>
                  <span className="font-semibold text-stone-800">
                    ~${p.estimated_cost_usd.toFixed(2)}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    data-testid={`preset-create-${p.id}`}
                    className="flex-1 bg-orange-500 text-white hover:bg-orange-600"
                    disabled={creatingId === p.id}
                    onClick={() => quickCreate(p)}
                  >
                    {creatingId === p.id ? (
                      <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                    ) : (
                      <Wand2 className="mr-1 h-4 w-4" />
                    )}
                    Quick create
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="border-orange-300 text-stone-700"
                  >
                    <Link
                      to={`/series/new?preset=${p.id}`}
                      data-testid={`preset-customize-${p.id}`}
                    >
                      Customize <ArrowRight className="ml-1 h-3 w-3" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="rounded-lg border border-orange-200 bg-orange-50/60 p-4 text-sm text-stone-700">
        <Sparkles className="mr-1 inline h-4 w-4 text-orange-600" />
        <strong>Strategy tip:</strong> stack 3–5 presets in
        <strong> Unlisted</strong> mode to build a 30-video backlog, then bulk
        flip to Public. YouTube's algorithm rewards channel depth.
      </div>
    </div>
  );
}
